import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import RichTextEditor from "../../../components/common/RichTextEditor";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import Swal from "sweetalert2";

const API_BASE = "https://tyka.premierhostings.com/beta/api";
const bannerTypes = ["main"];
const MAX_VIDEO_SIZE_MB = 50;
const MAX_VIDEO_SIZE = MAX_VIDEO_SIZE_MB * 1024 * 1024;

const EditChildCategory = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const page = searchParams.get("page") || 1;
  const per_page = searchParams.get("per_page") || 10;

  const mediaInputRef = useRef(null);

  const [headCategories, setHeadCategories] = useState([]);
  const [parentCategories, setParentCategories] = useState([]);
  const [filteredParentCategories, setFilteredParentCategories] = useState([]);

  // Category media (single image OR video)
  const [categoryMedia, setCategoryMedia] = useState(null); // new File (if chosen)
  const [categoryMediaPreview, setCategoryMediaPreview] = useState("");
  const [categoryType, setCategoryType] = useState(""); // "image" | "video" | ""
  const [removeCategoryMedia, setRemoveCategoryMedia] = useState(false);

  const [selectedHeadId, setSelectedHeadId] = useState("");
  const [parentCategoryId, setParentCategoryId] = useState("");
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [description, setDescription] = useState("");
  const [errors, setErrors] = useState({});
  const [banners, setBanners] = useState([
    { type: "main", image: null, preview: "" },
  ]);

  // Fetch every page of a paginated endpoint
  const fetchAllPages = async (url) => {
    let allData = [];
    let currentPage = 1;
    let lastPage = 1;

    try {
      do {
        const res = await axios.get(`${url}?page=${currentPage}`);
        allData = [...allData, ...(res.data.data || [])];
        lastPage = res.data.meta?.last_page || 1;
        currentPage++;
      } while (currentPage <= lastPage);
    } catch (err) {
      console.error("Error fetching all pages:", err);
    }

    return allData;
  };

  // Load head categories, all parent categories, and this child category
  useEffect(() => {
    const fetchData = async () => {
      try {
        const headsRes = await axios.get(`${API_BASE}/product-categories`);
        const heads = headsRes.data?.data || [];

        const parents = await fetchAllPages(
          `${API_BASE}/product-sub-categories`,
        );

        const childRes = await axios.get(
          `${API_BASE}/product-child-categories/${id}`,
        );
        const childData = childRes.data?.data;

        setHeadCategories(heads);
        setParentCategories(parents);

        if (childData) {
          const headId = childData.parent_category?.id?.toString() || "";
          const parentId = childData.child_category?.id?.toString() || "";

          setSelectedHeadId(headId);
          setParentCategoryId(parentId);

          setFilteredParentCategories(
            parents.filter((p) => p.product_category_id?.toString() === headId),
          );

          setName(childData.name || "");
          setSlug(childData.slug || "");
          setDescription(childData.description || "");

          // Keep existing category media visible while editing
          if (childData.video) {
            setCategoryMediaPreview(childData.video);
            setCategoryType("video");
          } else if (childData.category_image) {
            setCategoryMediaPreview(childData.category_image);
            setCategoryType("image");
          }

          const loadedBanners = (childData.banners || []).map((banner) => ({
            id: banner.id,
            type: banner.banner_type,
            image: null,
            preview: banner.image,
          }));
          if (loadedBanners.length > 0) setBanners(loadedBanners);
        }
      } catch (error) {
        toast.error("Failed to load data.");
        console.error("Fetch error:", error);
      }
    };

    fetchData();
  }, [id]);

  // Update filtered parents when head category changes
  useEffect(() => {
    if (selectedHeadId && parentCategories.length > 0) {
      const filtered = parentCategories.filter(
        (p) => p.product_category_id?.toString() === selectedHeadId,
      );
      setFilteredParentCategories(filtered);

      if (!filtered.some((p) => p.id.toString() === parentCategoryId)) {
        setParentCategoryId("");
      }
    } else {
      setFilteredParentCategories([]);
      setParentCategoryId("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedHeadId, parentCategories]);

  // Auto-generate slug from name
  useEffect(() => {
    const generatedSlug = name
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-");
    setSlug(generatedSlug);
  }, [name]);

  // Revoke any blob preview URLs when the component unmounts
  useEffect(() => {
    return () => {
      if (categoryMediaPreview.startsWith("blob:")) {
        URL.revokeObjectURL(categoryMediaPreview);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validateForm = () => {
    const newErrors = {};
    const nameRegex = /^[A-Za-z\s]+$/;

    if (!selectedHeadId) newErrors.head = "Please select a head category.";
    if (!parentCategoryId)
      newErrors.parent = "Please select a parent category.";
    if (!name.trim()) newErrors.name = "Name is required.";
    else if (!nameRegex.test(name))
      newErrors.name = "Name should only contain letters and spaces.";
    if (!slug.trim()) newErrors.slug = "Slug is required.";

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      Object.values(newErrors).forEach((err) => toast.error(err));
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      Swal.fire({
        title: "Updating Category...",
        text: "Please wait while we update the category.",
        allowOutsideClick: false,
        background: "#000",
        color: "#dc2626",
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const formData = new FormData();

      formData.append("product_category_id", selectedHeadId);
      formData.append("product_subcategory_id", parentCategoryId);
      formData.append("name", name);
      formData.append("slug", slug);
      formData.append("description", description);
      formData.append("category_id", selectedHeadId);

      // New category image/video as a normal file
      if (categoryMedia) {
        if (categoryType === "video") {
          formData.append("video", categoryMedia);
        } else {
          formData.append("category_image", categoryMedia);
        }
      }

      // Tell the backend to remove existing media (only if nothing new was chosen)
      if (removeCategoryMedia && !categoryMedia) {
        formData.append("remove_category_media", "1");
      }

      // Banner uploads
      // Banner uploads
      const bannersData = [];

      banners.forEach((banner, index) => {
        const bannerItem = {
          type: banner.type,
        };

        if (banner.id) {
          bannerItem.id = banner.id;
        }

        // Append the image file separately
        if (banner.image instanceof File) {
          formData.append(
            `banners[${index}][image]`,
            banner.image,
            banner.image.name,
          );

          bannerItem.has_new_image = true;
        } else {
          bannerItem.has_new_image = false;
        }

        bannersData.push(bannerItem);

        // Send banner type and ID as individual multipart fields
        formData.append(`banners[${index}][type]`, banner.type);

        if (banner.id) {
          formData.append(`banners[${index}][id]`, String(banner.id));
        }
      });

      // Send the populated banner metadata
      formData.append("banners", JSON.stringify(bannersData));

      // Laravel multipart update
      formData.append("_method", "PUT");

      console.log("Banner upload data:");
      for (const [key, value] of formData.entries()) {
        console.log(
          key,
          value instanceof File
            ? `${value.name} (${value.type}, ${value.size} bytes)`
            : value,
        );
      }

      const response = await axios.post(
        `${API_BASE}/product-child-categories/${id}`,
        formData,
      );

      Swal.close();
      toast.success("Child category updated successfully!");

      setTimeout(
        () => navigate(`/childcategory?page=${page}&per_page=${per_page}`),
        1500,
      );
    } catch (error) {
      Swal.close();

      toast.error(
        error.response?.data?.message || "Failed to update child category.",
      );

      console.error("Update error:", error.response?.data || error);
    }
  };

  const handleCategoryMedia = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const isImage = file.type.startsWith("image/");
    const isVideo = file.type.startsWith("video/");

    if (!isImage && !isVideo) {
      toast.error("Please select a valid image or video file.");
      e.target.value = "";
      return;
    }
    if (isVideo && file.size > MAX_VIDEO_SIZE) {
      toast.error(`Video size must be ${MAX_VIDEO_SIZE_MB}MB or less.`);
      e.target.value = "";
      return;
    }

    // Revoke previous local preview URL to avoid leaking memory
    if (categoryMediaPreview.startsWith("blob:")) {
      URL.revokeObjectURL(categoryMediaPreview);
    }

    setCategoryMedia(file);
    setCategoryMediaPreview(URL.createObjectURL(file));
    setCategoryType(isVideo ? "video" : "image");
    setRemoveCategoryMedia(false);
  };

  const handleDeleteCategoryMedia = async () => {
    const resetLocalMedia = () => {
      if (categoryMediaPreview.startsWith("blob:")) {
        URL.revokeObjectURL(categoryMediaPreview);
      }
      setCategoryMedia(null);
      setCategoryMediaPreview("");
      setCategoryType("");
      if (mediaInputRef.current) mediaInputRef.current.value = "";
    };

    // Newly picked file that isn't saved on the server yet: just clear it locally
    if (categoryMediaPreview.startsWith("blob:")) {
      resetLocalMedia();
      return;
    }

    // Existing media on the server: confirm, then call the API
    const result = await Swal.fire({
      title: `Delete ${categoryType}?`,
      text: "This will permanently remove it.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      confirmButtonText: "Yes, delete",
    });

    if (!result.isConfirmed) return;

    try {
      Swal.fire({
        title: "Deleting...",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      await axios.delete(`${API_BASE}/child-categories/${id}/video`);

      Swal.close();
      resetLocalMedia();
      toast.success("Deleted successfully!");
    } catch (error) {
      Swal.close();
      toast.error(error.response?.data?.message || "Failed to delete.");
      console.error("Delete media error:", error.response?.data || error);
    }
  };

  const handleBannerTypeChange = (index, value) => {
    setBanners((prev) =>
      prev.map((b, i) => (i === index ? { ...b, type: value } : b)),
    );
  };

  const handleBannerImageChange = (index, file) => {
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select a valid image.");
      return;
    }

    setBanners((prev) =>
      prev.map((banner, i) => {
        if (i !== index) return banner;

        if (banner.preview?.startsWith("blob:")) {
          URL.revokeObjectURL(banner.preview);
        }

        return {
          ...banner,
          image: file,
          preview: URL.createObjectURL(file),
        };
      }),
    );
  };

  const addBanner = () => {
    setBanners((prev) => [...prev, { type: "main", image: null, preview: "" }]);
  };

  const removeBanner = (index) => {
    setBanners((prev) => {
      const target = prev[index];
      if (target?.preview?.startsWith("blob:")) {
        URL.revokeObjectURL(target.preview);
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-8 dark:text-white">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
        <h1 className="text-2xl font-bold">Edit Child Category</h1>
      </div>

      <form
        onSubmit={handleSubmit}
        className="space-y-6 dark:bg-gray-800 rounded-lg p-6"
      >
        {/* Head Category */}
        <div>
          <label
            htmlFor="headCategory"
            className="block font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            Head Category <span className="text-red-500">*</span>
          </label>
          <select
            id="headCategory"
            value={selectedHeadId}
            onChange={(e) => setSelectedHeadId(e.target.value)}
            className={`w-full border rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-red-500 dark:bg-gray-700 dark:border-gray-600 ${
              errors.head ? "border-red-500" : ""
            }`}
          >
            <option value="">Select a Head Category</option>
            {headCategories.map((head) => (
              <option key={head.id} value={head.id.toString()}>
                {head.name}
              </option>
            ))}
          </select>
        </div>

        {/* Parent Category */}
        <div>
          <label
            htmlFor="parentCategory"
            className="block font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            Parent Category <span className="text-red-500">*</span>
          </label>
          <select
            id="parentCategory"
            value={parentCategoryId}
            onChange={(e) => setParentCategoryId(e.target.value)}
            className={`w-full border rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-red-500 dark:bg-gray-700 dark:border-gray-600 ${
              errors.parent ? "border-red-500" : ""
            }`}
            disabled={!selectedHeadId || filteredParentCategories.length === 0}
          >
            <option value="">Select a Parent Category</option>
            {filteredParentCategories.map((parent) => (
              <option key={parent.id} value={parent.id.toString()}>
                {parent.name}
              </option>
            ))}
          </select>
        </div>

        {/* Name */}
        <div>
          <label
            htmlFor="childCategoryName"
            className="block font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            Name <span className="text-red-500">*</span>
          </label>
          <Input
            id="childCategoryName"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={`${errors.name ? "border-red-500" : ""} w-full`}
          />
        </div>

        {/* Slug */}
        <div>
          <label
            htmlFor="slug"
            className="block font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            Slug <span className="text-red-500">*</span>
          </label>
          <Input
            id="slug"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            className={`${errors.slug ? "border-red-500" : ""} w-full`}
          />
        </div>

        {/* Description */}
        <div>
          <label
            htmlFor="description"
            className="block font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            Description
          </label>
          <RichTextEditor value={description} onChange={setDescription} />
        </div>

        {/* Category Image / Video */}
        <div className="w-full">
          <label
            htmlFor="category-media-input"
            className="block mb-2 font-medium"
          >
            Category Video
          </label>

          <input
            id="category-media-input"
            ref={mediaInputRef}
            type="file"
            accept="image/*,.mp4,.mov,.avi,.mkv,.webm"
            onChange={handleCategoryMedia}
            className="block w-full"
          />

          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            You can upload either one image or one video. Maximum video size is{" "}
            {MAX_VIDEO_SIZE_MB}MB.
          </p>

          {categoryType === "image" && categoryMediaPreview && (
            <div className="mt-3 flex w-full flex-col gap-3">
              <button
                type="button"
                onClick={handleDeleteCategoryMedia}
                className="self-end rounded bg-red-600 px-4 py-2 text-white hover:bg-red-700"
              >
                Delete Image
              </button>

              <img
                src={categoryMediaPreview}
                className="h-60 w-full rounded object-cover"
                alt="Category preview"
              />
            </div>
          )}

          {categoryType === "video" && categoryMediaPreview && (
            <div className="mt-3 flex w-full flex-col gap-3">
              <button
                type="button"
                onClick={handleDeleteCategoryMedia}
                className="self-end shrink-0 rounded bg-red-600 px-4 py-2 text-white hover:bg-red-700"
              >
                Delete Video
              </button>

              <video
                src={categoryMediaPreview}
                controls
                className="h-60 w-full rounded object-cover"
              />
            </div>
          )}
        </div>

        {/* Banners */}
        <div className="space-y-5">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Banners</h3>

            <Button
              type="button"
              onClick={addBanner}
              className="bg-green-600 hover:bg-green-700"
            >
              + Add More Banner
            </Button>
          </div>

          {banners.map((banner, index) => (
            <div
              key={banner.id ?? `new-${index}`}
              className="border rounded-lg p-4 space-y-4"
            >
              <div>
                <label className="block mb-2 font-medium">Banner Type</label>

                <select
                  value={banner.type}
                  onChange={(e) =>
                    handleBannerTypeChange(index, e.target.value)
                  }
                  className="w-full border rounded-lg p-2 dark:bg-gray-700 dark:border-gray-600"
                >
                  {bannerTypes.map((type) => (
                    <option key={type} value={type}>
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block mb-2 font-medium">Banner Image</label>

                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) =>
                    handleBannerImageChange(index, e.target.files?.[0])
                  }
                  className="block w-full border rounded-lg p-2"
                />

                {banner.preview && (
                  <img
                    src={banner.preview}
                    alt="Banner preview"
                    className="mt-3 h-40 rounded border object-cover"
                  />
                )}
              </div>

              <Button
                type="button"
                onClick={() => removeBanner(index)}
                className="bg-red-600 hover:bg-red-700"
              >
                Remove
              </Button>
            </div>
          ))}
        </div>

        {/* Buttons */}
        <div className="flex flex-wrap justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button
            type="button"
            variant="secondary"
            onClick={() =>
              navigate(`/childcategory?page=${page}&per_page=${per_page}`)
            }
            className="bg-gray-300 hover:bg-gray-400 dark:bg-gray-700 dark:hover:bg-gray-600"
          >
            Cancel
          </Button>
          <Button type="submit" className="bg-red-600 hover:bg-red-700">
            Update
          </Button>
        </div>
      </form>

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default EditChildCategory;
